import React, { useRef } from 'react';
import * as d3 from 'd3';
import { Box, List } from '@mui/material';
import MenuItemRow from '../MenuItemRow';
import { PersonIcon, SpouseIcon } from '../MenuIcons';
import { MENU_TONE } from '../menuTheme';
import { usePedigreeInstance } from '../../pedigree/instanceContext';
import { config } from '../../pedigree/config';
import {
  linkChildRelation,
  linkSiblingRelation
} from '../../pedigree/edgeIntegrity';
import { RELATIONS } from '../../../pedigree-utils';
import { APICONSTANTS, PEDIGREE } from 'src/components/Constants';
import { GENDER } from 'src/components/questionnaire/Constants';
import axios from 'src/axios';
import {
  clearSelectionHighlight,
  highlightSelectedNode
} from '../../selectionHighlight';

function SetRelation({
  node,
  user,
  handleClose,
  memberRecords,
  setMemberRecords,
  setPedigreeState,
  selectionModeRef,
  handleSelectionExit,
  setError
}) {
  // Scope node/canvas lookups to this chart (multi-pedigree-tabs). With no
  // container (lone chart) `scope()` is the bare d3 module, so the calls are
  // exactly `d3.selectAll('.te')` / `d3.select('.canvas')` as before; with a
  // container it scopes to that chart's subtree.
  const { containerRef } = usePedigreeInstance();
  const scope = () =>
    containerRef.current ? d3.select(containerRef.current) : d3;
  const canvas = scope().select('.canvas');

  const relationType = useRef(null);

  const getPerson = (x) => {
    if (!x) return undefined;
    if (x?.pid !== undefined && x?.pid !== null) return x;
    if (x?.data?.pid !== undefined && x?.data?.pid !== null) return x.data;
    return undefined;
  };

  const isUnion = (p) => p?.kind === config?.NODE_TYPE?.UNION;

  const menuPerson = getPerson(node);

  const markUnsaved = () => {
    setPedigreeState?.((s) => ({
      ...s,
      save: PEDIGREE.saveStatus.IN_PROGRESS
    }));
  };

  const updatePedigreeChartNode = async (updatedNodes) => {
    try {
      await axios.post(
        APICONSTANTS.updatePedigreeRelaiton.replace(':user_id', user._id),
        { config: updatedNodes }
      );
    } catch (e) {
      setError?.('ERROR: Updates not saved - please try again');
    }
  };

  const updateData = (next) => {
    const cloned = JSON.parse(JSON.stringify(next));
    setMemberRecords(cloned);
    setPedigreeState?.((s) => ({ ...s }));
  };

  const adoptExistingChildrenIntoNewUnion = (
    deepCopy,
    parent,
    newSpouse,
    originalChildIds,
    updatedIndices
  ) => {
    if (!originalChildIds.length) return;

    originalChildIds.forEach((childPid) => {
      const childIdx = deepCopy.findIndex((p) => p.pid === childPid);
      if (childIdx < 0) return;
      const child = deepCopy[childIdx];

      if (parent.gender === GENDER.male && !child.motherId) {
        child.motherId = newSpouse.pid;
      } else if (parent.gender === GENDER.female && !child.fatherId) {
        child.fatherId = newSpouse.pid;
      } else {
        return;
      }

      if (!newSpouse.childrenIds.includes(childPid)) {
        newSpouse.childrenIds.push(childPid);
      }

      deepCopy[childIdx] = child;
      updatedIndices.add(childIdx);
    });
  };

  const cleanupSelectionMode = () => {
    // Re-select fresh at call time — the render-time `canvas`/`nodes` selections
    // go stale if the SVG was rebuilt while waiting for the second click.
    scope().select('.canvas').style('fill', 'white');
    // (scope() is the d3 module when unscoped, else this chart's selection)

    // Fresh selection, and a restore that reads back what the canvas had rather
    // than asserting a value — see selectionHighlight.js.
    clearSelectionHighlight(containerRef.current);

    if (selectionModeRef) selectionModeRef.current = false;
    handleSelectionExit?.(false);
  };

  async function setRelationAsSpouse(startNodeRaw, selectedNodeRaw) {
    const aRaw = getPerson(startNodeRaw);
    const bRaw = getPerson(selectedNodeRaw);

    if (!aRaw?.pid || !bRaw?.pid) {
      setError?.('Set Relation failed: pid missing on selected node(s).');
      return;
    }

    if (aRaw.pid === bRaw.pid) return;

    if (isUnion(aRaw) || isUnion(bRaw)) {
      setError?.('Relation Not Allowed : Please select person nodes only');
      return;
    }

    const deepCopy = JSON.parse(JSON.stringify(memberRecords || []));
    const aIdx = deepCopy.findIndex((p) => p.pid === aRaw.pid);
    const bIdx = deepCopy.findIndex((p) => p.pid === bRaw.pid);
    if (aIdx < 0 || bIdx < 0) return;

    const a = deepCopy[aIdx];
    const b = deepCopy[bIdx];

    a.spouseIds = Array.isArray(a.spouseIds) ? a.spouseIds : [];
    b.spouseIds = Array.isArray(b.spouseIds) ? b.spouseIds : [];

    if (a.spouseIds.includes(b.pid) && b.spouseIds.includes(a.pid)) return;

    const aHadNoSpouse = a.spouseIds.length === 0;
    const bHadNoSpouse = b.spouseIds.length === 0;

    const depthCache = new Map();
    const computeDepth = (pid) => {
      if (!pid) return 0;
      if (depthCache.has(pid)) return depthCache.get(pid);
      depthCache.set(pid, -1); // cycle sentinel
      const p = deepCopy.find((m) => m.pid === pid);
      if (!p) {
        depthCache.set(pid, 0);
        return 0;
      }
      const momDepth = p.motherId != null ? computeDepth(p.motherId) : -1;
      const dadDepth = p.fatherId != null ? computeDepth(p.fatherId) : -1;
      const depth = Math.max(momDepth, dadDepth) + 1;
      depthCache.set(pid, depth);
      return depth;
    };
    const isCrossLevel = computeDepth(a.pid) !== computeDepth(b.pid);

    const aOriginalChildrenIds =
      aHadNoSpouse && !isCrossLevel ? [...(a.childrenIds || [])] : [];
    const bOriginalChildrenIds =
      bHadNoSpouse && !isCrossLevel ? [...(b.childrenIds || [])] : [];

    a.spouseIds = Array.from(new Set([...a.spouseIds, b.pid]));
    b.spouseIds = Array.from(new Set([...b.spouseIds, a.pid]));

    if (isCrossLevel) {
      a.crossLevelSpouseIds = Array.from(
        new Set([...(a.crossLevelSpouseIds || []), b.pid])
      );
      b.crossLevelSpouseIds = Array.from(
        new Set([...(b.crossLevelSpouseIds || []), a.pid])
      );
    }

    a.spouse = b.pid;
    b.spouse = a.pid;

    a.childrenIds = Array.isArray(a.childrenIds) ? a.childrenIds : [];
    b.childrenIds = Array.isArray(b.childrenIds) ? b.childrenIds : [];

    deepCopy[aIdx] = a;
    deepCopy[bIdx] = b;

    const updatedIndices = new Set([aIdx, bIdx]);

    adoptExistingChildrenIntoNewUnion(
      deepCopy,
      a,
      b,
      aOriginalChildrenIds,
      updatedIndices
    );
    adoptExistingChildrenIntoNewUnion(
      deepCopy,
      b,
      a,
      bOriginalChildrenIds,
      updatedIndices
    );

    markUnsaved();

    await updatePedigreeChartNode(
      [...updatedIndices].map((idx) => ({ data: deepCopy[idx], index: idx }))
    );

    updateData(deepCopy);
  }

  const persistTouched = async (members, touchedPids) => {
    markUnsaved();
    await updatePedigreeChartNode(
      touchedPids.map((pid) => {
        const index = members.findIndex((p) => p.pid === pid);
        return { data: members[index], index };
      })
    );
    updateData(members);
  };

  const setRelationAsChild = async (startNodeRaw, selectedNodeRaw) => {
    const childRaw = getPerson(startNodeRaw);
    const parentRaw = getPerson(selectedNodeRaw);

    if (!childRaw?.pid || !parentRaw?.pid) {
      setError?.('Set Relation failed: pid missing on selected node(s).');
      return;
    }

    if (childRaw.pid === parentRaw.pid) return;

    if (isUnion(childRaw) || isUnion(parentRaw)) {
      setError?.('Relation Not Allowed : Please select person nodes only');
      return;
    }

    const result = linkChildRelation(
      memberRecords || [],
      childRaw.pid,
      parentRaw.pid
    );
    if (result.error) {
      setError?.(result.error);
      return;
    }
    if (!result.members) return;

    await persistTouched(result.members, result.touchedPids);
  };

  const setRelationAsSibling = async (startNodeRaw, selectedNodeRaw) => {
    const currentRaw = getPerson(startNodeRaw);
    const siblingRaw = getPerson(selectedNodeRaw);

    if (!currentRaw?.pid || !siblingRaw?.pid) {
      setError?.('Set Relation failed: pid missing on selected node(s).');
      return;
    }

    if (currentRaw.pid === siblingRaw.pid) return;

    if (isUnion(currentRaw) || isUnion(siblingRaw)) {
      setError?.('Relation Not Allowed : Please select person nodes only');
      return;
    }

    const result = linkSiblingRelation(
      memberRecords || [],
      currentRaw.pid,
      siblingRaw.pid
    );
    if (result.error) {
      setError?.(result.error);
      return;
    }
    if (!result.members) return;

    await persistTouched(result.members, result.touchedPids);
  };

  async function validateAndSetRelation(startNodeRaw, selectedNodeRaw) {
    switch (relationType.current) {
      case RELATIONS.partner:
        await setRelationAsSpouse(startNodeRaw, selectedNodeRaw);
        break;
      case RELATIONS.children:
        await setRelationAsChild(startNodeRaw, selectedNodeRaw);
        break;
      case RELATIONS.siblings:
        await setRelationAsSibling(startNodeRaw, selectedNodeRaw);
        break;
      default:
        break;
    }
  }

  async function handleSelectRelation(node_1, node_2) {
    const first = getPerson(node_1);
    const second = getPerson(node_2);

    if (first?.pid && second?.pid && first.pid === second.pid) {
      cleanupSelectionMode();
      return;
    }

    try {
      await validateAndSetRelation(node_1, node_2);
    } finally {
      cleanupSelectionMode();
    }
  }

  const specifyRelation = (relation) => {
    relationType.current = relation;

    if (selectionModeRef) selectionModeRef.current = true;

    // The second click is completed by ChartBox's persistent node-click handler
    // (which Canvas re-binds on every SVG rebuild), NOT by an imperative d3 bind
    // on the current DOM nodes: a relayout mid-selection used to orphan that
    // bind and permanently freeze all node clicks (selectionModeRef stuck true).
    handleSelectionExit?.({
      node: menuPerson,
      relation,
      complete: (targetDatum) => handleSelectRelation(node, targetDatum)
    });

    canvas.style('fill', '#eeeeee');
    // Was `.25em`, which resolves against the path's font size and came out
    // THINNER than an ordinary node on a chart drawn at stroke-width 10.
    highlightSelectedNode(containerRef.current, menuPerson?.pid);

    handleClose?.();
  };

  return (
    <Box>
      {/* Each of these arms a two-click selection mode rather than doing
          something, which is what the parent row's ellipsis signals. */}
      <List disablePadding>
        <MenuItemRow
          icon={<SpouseIcon />}
          tone={MENU_TONE.relationship}
          label="Spouse"
          onClick={() => specifyRelation(RELATIONS.partner)}
        />
        <MenuItemRow
          icon={<PersonIcon />}
          tone={MENU_TONE.relationship}
          label="Sibling"
          onClick={() => specifyRelation(RELATIONS.siblings)}
        />
        {/* "Parent", not "Child". linkChildRelation is called with the selected
            person as the CHILD and the node you click next as the PARENT, so this
            links the selected person TO a parent — which is what you are picking.
            Labelling it "Child" read as "add a child" and had the direction exactly
            backwards (TGCLOUD-1643 QA). No "(child of)" gloss: the banner states
            the direction in full once selection mode is armed, and a bracketed
            second reading in a menu row is more to parse, not less. */}
        <MenuItemRow
          icon={<PersonIcon />}
          tone={MENU_TONE.relationship}
          label="Parent"
          onClick={() => specifyRelation(RELATIONS.children)}
        />
      </List>
    </Box>
  );
}

export default SetRelation;
